import { Button, type TableColumnsType } from 'antd';
import moment from 'moment';
import React from 'react';
import type { Fetcher } from 'swr';
import { z } from 'zod';

import axiosWithAuth from '@/lib/axios';
import { Feedback } from '@/types/database/feedback';
import { realtimeMessagesSchema } from '@/types/database/realtimeMessages';

import type { TableData } from './types';

export const getColumns = (checkoutFeedback: (feedback: TableData) => void) => {
  const columns: TableColumnsType<TableData> = [
    {
      dataIndex: 'createdTime',
      key: 'createdTime',
      title: '建立時間',
      render: (createdTime) => moment(createdTime).format('YYYY-MM-DD HH:mm'),
    },
    {
      dataIndex: 'type',
      key: 'type',
      title: '類別',
    },
    {
      dataIndex: 'householdNumber',
      key: 'householdNumber',
      title: '戶號',
    },
    {
      dataIndex: 'title',
      key: 'title',
      title: '標題',
    },
    {
      dataIndex: 'status',
      key: 'status',
      title: '狀態',
    },
    {
      key: 'action',
      title: '操作',
      render: (_, feedbackData) => (
        <Button onClick={() => checkoutFeedback(feedbackData)}>查看更多</Button>
      ),
    },
  ];
  return columns;
};

const messagesSchema = z.array(
  z.object({
    entityId: z.string(),
    content: realtimeMessagesSchema.element,
  })
);

type Messages = z.infer<typeof messagesSchema>;

export const messagesFetcher: Fetcher<Messages> = async (url: string) => {
  const res = await axiosWithAuth.post(url, {
    service: 'realtimeMessages',
    aggregate: [
      { $sort: { createdTime: -1 } },
      {
        $group: {
          _id: '$entityId',
          lastMessage: { $first: '$$ROOT' },
        },
      },
      {
        $project: {
          _id: 0,
          entityId: '$_id',
          content: '$lastMessage',
        },
      },
    ],
  });
  const messagesStatus = messagesSchema.parse(res.data);
  return messagesStatus;
};

type GetDisplayFeedbackDataArgs = {
  feedbacks: Feedback[];
  lastMessages: Messages;
  userId: string;
  tabKey: '未讀' | '已讀';
};

export const getDisplayFeedbackData = ({
  feedbacks,
  lastMessages,
  userId,
  tabKey,
}: GetDisplayFeedbackDataArgs) => {
  const formedFeedbacks = feedbacks?.map((feedback) => {
    const lastMessage = lastMessages?.find(
      (message) => message.entityId === feedback._id
    );
    const isReplied = lastMessage?.content?.sender?.id === userId;
    const isRead =
      isReplied ||
      (lastMessage?.content.sender.id !== userId &&
        lastMessage?.content?.isRead);
    return {
      ...feedback,
      key: feedback._id,
      status: isReplied ? '已回覆' : '未回覆',
      isRead,
    };
  });

  const filteredData = formedFeedbacks?.filter((feedback) => {
    if (tabKey === '未讀') {
      return !feedback.isRead;
    } else if (tabKey === '已讀') {
      return feedback.isRead;
    }
    return true;
  });

  return filteredData;
};
